import {
  TextInput,
  Button,
  Group,
  Stack,
  Select,
  Grid,
  Box
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { AddressService } from '../../types';

export interface AddressFormValues {
  id: string | any;
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface AddressFormProps {
  initialValues?: AddressFormValues;
  onSubmit: (values: AddressFormValues) => void;
  onCancel?: () => void;
  isLoading?: boolean;
}


const countryOptions = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'UK', label: 'United Kingdom' },
  { value: 'AU', label: 'Australia' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'JP', label: 'Japan' },
  { value: 'SG', label: 'Singapore' },
  { value: 'MV', label: 'Maldives' },
];

const defaultAddress: AddressFormValues = {
  id: undefined,
  address: '',
  city: '',
  state: '',
  zip: '',
  country: 'MV', // Default to Maldives
};

const AddressForm = ({
  initialValues = defaultAddress,
  onSubmit,
  onCancel,
  isLoading = false,
}: AddressFormProps) => {
  const form = useForm<AddressFormValues>({
    initialValues,
    validate: {
      address: (value) => (value.trim().length === 0 ? 'Address is required' : null),
      city: (value) => (value.trim().length === 0 ? 'City is required' : null),
      state: (value) => (value.trim().length === 0 ? 'State/Province is required' : null),
      zip: (value) => (value.trim().length === 0 ? 'ZIP code is required' : null),
      country: (value) => (value.trim().length === 0 ? 'Country is required' : null),
    },
  });

  const handleSubmit = async (values: AddressFormValues) => {
    try {
      // Create new address
      const response = await AddressService.postAddress({
        address: values.address,
        city: values.city,
        state: values.state,
        zip: values.zip,
        country: values.country
      });

      notifications.show({
        title: 'Success',
        message: 'Address added successfully!',
        color: 'green',
      });

      // Map API response to AddressFormValues format
      const mappedResponse: AddressFormValues = {
        id: response.id || response.address?.id,
        address: response.address || values.address,
        city: response.city || values.city,
        state: response.state || values.state,
        zip: response.zip || values.zip,
        country: response.country || values.country
      };

      // Pass the properly formatted values back to parent component
      onSubmit(mappedResponse);

    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to add address. Please try again.',
        color: 'red',
      });
    }
  };

  return (
    <Box>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Group justify="space-between" align="center">
          </Group>

          <Grid gutter="md">
            <Grid.Col span={{ base: 12, md: 12 }}>
              <TextInput
                label="Address"
                placeholder="123 Main St"
                required
                {...form.getInputProps('address')}
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput
                label="City"
                placeholder="New York"
                required
                {...form.getInputProps('city')}
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput
                label="State/Province"
                placeholder="NY"
                required
                {...form.getInputProps('state')}
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TextInput
                label="ZIP Code"
                placeholder="10001"
                required
                {...form.getInputProps('zip')}
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Select
                label="Country"
                placeholder="Select a country"
                data={countryOptions}
                required
                searchable
                {...form.getInputProps('country')}
              />
            </Grid.Col>
          </Grid>

          <Group justify="flex-end" mt="md">
            {onCancel && (
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button type="submit" loading={isLoading}>
              {isLoading ? 'Saving...' : 'Add Address'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Box>
  );
};

export default AddressForm;
